/**
 * Serviço para integração com FIPE via Edge Function.
 * Funções para consulta de valores FIPE e auxiliares.
 */

import { supabase } from "@/integrations/supabase/client"
import { getFipeLookupUserError } from "@/lib/vehicleLookupMessages"

export type OrigemFIPE = 'api' | 'manual'

export interface HistoricoFIPE {
  mes: string
  valor: number
}

export interface ConsultaFIPEResponse {
  error: boolean
  message: string
  valorFIPE?: string
  valorNumerico?: number
  historicoFIPE?: HistoricoFIPE[]
}

export interface HistoricoFIPEBanco {
  id: string
  veiculo_id: string
  valor_fipe: number
  origem: OrigemFIPE
  data_consulta: string
  placa_consultada: string
  created_at: string
  updated_at: string
}

export interface UltimoValorFIPE {
  valor_fipe: number
  origem: OrigemFIPE
}

// Função para validar o formato da placa brasileira
export const validarPlaca = (placa: string): boolean => {
  // Formato brasileiro: AAA0A00 ou AAA0000
  const regexPlaca = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/i
  return regexPlaca.test(placa.toUpperCase().replace(/[^a-zA-Z0-9]/g, ''))
}

// Função para salvar consulta FIPE no histórico
export const salvarHistoricoFIPE = async (
  veiculoId: string, 
  valorFIPE: number, 
  placaConsultada: string,
  origem: OrigemFIPE = 'api'
): Promise<boolean> => {
  try {
    const payloadBase = {
      veiculo_id: veiculoId,
      valor_fipe: valorFIPE,
      placa_consultada: placaConsultada,
    }

    const { data, error } = await supabase
      .from('historico_fipe')
      .insert(payloadBase)
      .select('*')
      .single()

    if (error) {
      console.error('Erro ao salvar histórico FIPE:', error)
      return false
    }

    if (data && 'origem' in data && data.origem !== origem) {
      const { error: origemError } = await supabase
        .from('historico_fipe')
        .update({ origem })
        .eq('id', data.id)

      if (origemError) {
        console.error('Erro ao atualizar origem do histórico FIPE:', origemError)
        return false
      }
    }

    console.log('Histórico FIPE salvo com sucesso')
    return true
  } catch (error) {
    console.error('Erro inesperado ao salvar histórico FIPE:', error)
    return false
  }
}

// Função para buscar histórico FIPE de um veículo
export const buscarHistoricoFIPE = async (veiculoId: string): Promise<HistoricoFIPEBanco[]> => {
  try {
    const { data, error } = await supabase
      .from('historico_fipe')
      .select('*')
      .eq('veiculo_id', veiculoId)
      .order('data_consulta', { ascending: false })

    if (error) {
      console.error('Erro ao buscar histórico FIPE:', error)
      return []
    }

    return (data || []).map((item) => ({
      ...item,
      origem: (item.origem || 'api') as OrigemFIPE,
    }))
  } catch (error) {
    console.error('Erro inesperado ao buscar histórico FIPE:', error)
    return []
  }
}

export const buscarUltimoRegistroFIPE = async (veiculoId: string): Promise<UltimoValorFIPE | null> => {
  try {
    const { data, error } = await supabase
      .from('historico_fipe')
      .select('*')
      .eq('veiculo_id', veiculoId)
      .order('data_consulta', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Erro ao buscar último valor FIPE:', error)
      return null
    }

    return data?.valor_fipe ? {
      valor_fipe: data.valor_fipe,
      origem: (data.origem || 'api') as OrigemFIPE,
    } : null
  } catch (error) {
    console.error('Erro inesperado ao buscar último valor FIPE:', error)
    return null
  }
}

// Função para buscar o último valor FIPE de um veículo
export const buscarUltimoValorFIPE = async (veiculoId: string): Promise<number | null> => {
  const ultimo = await buscarUltimoRegistroFIPE(veiculoId)
  return ultimo?.valor_fipe ?? null
}

// Função para consultar valor FIPE pela placa
export const consultarValorFIPE = async (placa: string, veiculoId?: string): Promise<ConsultaFIPEResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke('consultar-fipe', {
      body: { placa, veiculoId },
    })

    if (error) {
      let message = error.message || "Erro ao consultar o valor FIPE."
      try {
        const context = (error as { context?: { json?: () => Promise<{ message?: string; error?: string }> } }).context
        const body = await context?.json?.()
        message = body?.message || body?.error || message
      } catch {
        // Mantém a mensagem padrão quando o corpo do erro não está disponível.
      }

      return {
        error: true,
        message: getFipeLookupUserError(message).message,
      }
    }

    return {
      error: Boolean(data?.error),
      message: data?.error ? getFipeLookupUserError(data?.message).message : "",
      valorFIPE: data?.valorFIPE,
      valorNumerico: data?.valorNumerico,
      historicoFIPE: data?.historicoFIPE,
    }
  } catch (error) {
    return {
      error: true,
      message: getFipeLookupUserError().message,
    }
  }
}

// Função para buscar marcas disponíveis na FIPE
export const buscarMarcasFIPE = async (): Promise<string[]> => {
  try {
    // Simulação - em produção, faria a chamada real à API
    await new Promise(resolve => setTimeout(resolve, 500))
    
    return [
      "Fiat", "Volkswagen", "Chevrolet", "Ford", "Toyota",
      "Honda", "Hyundai", "Renault", "Nissan", "Jeep"
    ]
  } catch (error) {
    console.error('Erro ao buscar marcas:', error)
    return []
  }
}

// Função para buscar modelos de uma marca específica
export const buscarModelosFIPE = async (marca: string): Promise<string[]> => {
  try {
    // Simulação - em produção, faria a chamada real à API
    await new Promise(resolve => setTimeout(resolve, 500))
    
    const modelosPorMarca: Record<string, string[]> = {
      "Fiat": ["Uno", "Palio", "Siena", "Strada", "Toro"],
      "Volkswagen": ["Gol", "Voyage", "Polo", "Virtus", "T-Cross"],
      "Chevrolet": ["Onix", "Prisma", "Cruze", "Tracker", "S10"],
      "Ford": ["Ka", "Fiesta", "Focus", "Ranger", "EcoSport"]
    }

    return modelosPorMarca[marca] || []
  } catch (error) {
    console.error('Erro ao buscar modelos:', error)
    return []
  }
}
